"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesAccess = void 0;
const ttag_1 = require("ttag");
const interface_1 = require("../../interface");
const errors_1 = require("../../errors");
const errors_2 = require("../errors");
const batchLoading_1 = require("../batchLoading");
const uids_1 = require("../uids");
const extendedAttributes_1 = require("./extendedAttributes");
const validations_1 = require("./validations");
const mediaTypes_1 = require("./mediaTypes");
/**
 * Provides access to node metadata.
 *
 * The node access module is responsible for fetching, decrypting and caching
 * nodes metadata.
 */
class NodesAccess {
    logger;
    apiService;
    cache;
    cryptoCache;
    cryptoService;
    shareService;
    constructor(logger, apiService, cache, cryptoCache, cryptoService, shareService) {
        this.logger = logger;
        this.apiService = apiService;
        this.cache = cache;
        this.cryptoCache = cryptoCache;
        this.cryptoService = cryptoService;
        this.shareService = shareService;
        this.logger = logger;
        this.apiService = apiService;
        this.cache = cache;
        this.cryptoCache = cryptoCache;
        this.cryptoService = cryptoService;
        this.shareService = shareService;
    }
    async getMyFilesRootFolder() {
        const { volumeId, rootNodeId } = await this.shareService.getMyFilesIDs();
        const nodeUid = (0, uids_1.makeNodeUid)(volumeId, rootNodeId);
        return this.getNode(nodeUid);
    }
    async getNode(nodeUid) {
        let cachedNode;
        try {
            cachedNode = await this.cache.getNode(nodeUid);
        }
        catch { }
        if (cachedNode && !cachedNode.isStale) {
            return cachedNode;
        }
        this.logger.debug(`Node ${nodeUid} is ${cachedNode?.isStale ? 'stale' : 'not cached'}`);
        const { node } = await this.loadNode(nodeUid);
        return node;
    }
    async *iterateFolderChildren(parentNodeUid, signal) {
        // Ensure the parent is loaded and up-to-date.
        const parentNode = await this.getNode(parentNodeUid);
        const batchLoading = new batchLoading_1.BatchLoading({ iterateItems: (nodeUids) => this.loadNodes(nodeUids, signal) });
        const areChildrenCached = await this.cache.isFolderChildrenLoaded(parentNodeUid);
        if (areChildrenCached) {
            for await (const node of this.cache.iterateChildren(parentNodeUid)) {
                if (node.ok && !node.node.isStale) {
                    yield node.node;
                }
                else {
                    yield* batchLoading.load(node.uid);
                }
            }
            yield* batchLoading.loadRest();
            return;
        }
        this.logger.debug(`Folder ${parentNodeUid} children are not cached`);
        for await (const nodeUid of this.apiService.iterateChildrenNodeUids(parentNode.uid, signal)) {
            let node;
            try {
                node = await this.cache.getNode(nodeUid);
            }
            catch { }
            if (node && !node.isStale) {
                yield node;
            }
            else {
                this.logger.debug(`Node ${nodeUid} from ${parentNodeUid} is ${node?.isStale ? 'stale' : 'not cached'}`);
                yield* batchLoading.load(nodeUid);
            }
        }
        yield* batchLoading.loadRest();
        await this.cache.setFolderChildrenLoaded(parentNodeUid);
    }
    // Improvement requested: keep status of loaded trash and leverage cache.
    async *iterateTrashedNodes(signal) {
        const { volumeId } = await this.shareService.getMyFilesIDs();
        const batchLoading = new batchLoading_1.BatchLoading({ iterateItems: (nodeUids) => this.loadNodes(nodeUids, signal) });
        for await (const nodeUid of this.apiService.iterateTrashedNodeUids(volumeId, signal)) {
            let node;
            try {
                node = await this.cache.getNode(nodeUid);
            }
            catch { }
            if (node && !node.isStale) {
                yield node;
            }
            else {
                this.logger.debug(`Node ${nodeUid} trom trash is ${node?.isStale ? 'stale' : 'not cached'}`);
                yield* batchLoading.load(nodeUid);
            }
        }
        yield* batchLoading.loadRest();
    }
    async *iterateNodes(nodeUids, signal) {
        const batchLoading = new batchLoading_1.BatchLoading({ iterateItems: (nodeUids) => this.loadNodesWithMissingReport(nodeUids, signal) });
        for await (const result of this.cache.iterateNodes(nodeUids)) {
            if (result.ok && !result.node.isStale) {
                yield result.node;
            }
            else {
                yield* batchLoading.load(result.uid);
            }
        }
        yield* batchLoading.loadRest();
    }
    async loadNode(nodeUid) {
        const encryptedNode = await this.apiService.getNode(nodeUid);
        return this.decryptNode(encryptedNode);
    }
    async *loadNodes(nodeUids, signal) {
        for await (const result of this.loadNodesWithMissingReport(nodeUids, signal)) {
            if ('missingUid' in result) {
                continue;
            }
            yield result;
        }
    }
    async *loadNodesWithMissingReport(nodeUids, signal) {
        const returnedNodeUids = [];
        const errors = [];
        for await (const encryptedNode of this.apiService.iterateNodes(nodeUids, signal)) {
            returnedNodeUids.push(encryptedNode.uid);
            try {
                const { node } = await this.decryptNode(encryptedNode);
                yield node;
            }
            catch (error) {
                errors.push(error);
            }
        }
        if (errors.length > 0) {
            this.logger.error(`Failed to decrypt ${errors.length} nodes`, errors);
            throw new errors_1.ProtonDriveError((0, ttag_1.c)('Error').t `Failed to decrypt some nodes`, { cause: errors });
        }
        const missingNodeUids = nodeUids.filter((nodeUid) => !returnedNodeUids.includes(nodeUid));
        if (missingNodeUids.length) {
            this.logger.debug(`Removing ${missingNodeUids.length} nodes from cache not existing on the API anymore`);
            await this.cache.removeNodes(missingNodeUids);
            for (const missingNodeUid of missingNodeUids) {
                yield { missingUid: missingNodeUid };
            }
        }
    }
    async decryptNode(encryptedNode) {
        let parentKey;
        try {
            const parentKeys = await this.getParentKeys(encryptedNode);
            parentKey = parentKeys.key;
        }
        catch (error) {
            if (error instanceof errors_1.DecryptionError) {
                return {
                    node: {
                        ...encryptedNode,
                        isStale: false,
                        name: (0, interface_1.resultError)(error),
                        keyAuthor: (0, interface_1.resultError)({
                            claimedAuthor: encryptedNode.encryptedCrypto.signatureEmail,
                            error: (0, errors_2.getErrorMessage)(error),
                        }),
                        nameAuthor: (0, interface_1.resultError)({
                            claimedAuthor: encryptedNode.encryptedCrypto.nameSignatureEmail,
                            error: (0, errors_2.getErrorMessage)(error),
                        }),
                        errors: [error],
                    },
                };
            }
            throw error;
        }
        const { node: unparsedNode, keys } = await this.cryptoService.decryptNode(encryptedNode, parentKey);
        const node = await this.parseNode(unparsedNode);
        try {
            await this.cache.setNode(node);
        }
        catch (error) {
            this.logger.error(`Failed to cache node ${node.uid}`, error);
        }
        if (keys) {
            try {
                await this.cryptoCache.setNodeKeys(node.uid, keys);
            }
            catch (error) {
                this.logger.error(`Failed to cache node keys ${node.uid}`, error);
            }
        }
        return { node, keys };
    }
    async parseNode(unparsedNode) {
        let nodeName = unparsedNode.name;
        if (unparsedNode.name.ok) {
            try {
                (0, validations_1.validateNodeName)(unparsedNode.name.value);
            }
            catch (error) {
                this.logger.warn(`Node name validation failed: ${error instanceof Error ? error.message : error}`);
                nodeName = (0, interface_1.resultError)({
                    name: unparsedNode.name.value,
                    error: error instanceof Error ? error.message : (0, ttag_1.c)('Error').t `Unknown error`,
                });
            }
        }
        if (unparsedNode.type === interface_1.NodeType.File) {
            const extendedAttributes = unparsedNode.activeRevision?.ok
                ? (0, extendedAttributes_1.parseFileExtendedAttributes)(this.logger, unparsedNode.activeRevision.value.extendedAttributes)
                : undefined;
            return {
                ...unparsedNode,
                isStale: false,
                activeRevision: !unparsedNode.activeRevision?.ok ? unparsedNode.activeRevision : (0, interface_1.resultOk)({
                    uid: unparsedNode.activeRevision.value.uid,
                    state: unparsedNode.activeRevision.value.state,
                    creationTime: unparsedNode.activeRevision.value.creationTime,
                    storageSize: unparsedNode.activeRevision.value.storageSize,
                    contentAuthor: unparsedNode.activeRevision.value.contentAuthor,
                    thumbnails: unparsedNode.activeRevision.value.thumbnails,
                    ...extendedAttributes,
                }),
                folder: undefined,
            };
        }
        const extendedAttributes = unparsedNode.folder?.extendedAttributes
            ? (0, extendedAttributes_1.parseFolderExtendedAttributes)(this.logger, unparsedNode.folder.extendedAttributes)
            : undefined;
        return {
            ...unparsedNode,
            name: nodeName,
            isStale: false,
            activeRevision: undefined,
            folder: extendedAttributes ? {
                ...extendedAttributes,
            } : undefined,
        };
    }
    async getParentKeys(node) {
        if (node.parentUid) {
            try {
                return await this.getNodeKeys(node.parentUid);
            }
            catch (error) {
                if (error instanceof errors_1.DecryptionError) {
                    // Change the error message to be more specific.
                    // Original error message is referring to node, while here
                    // it referes to as parent to follow the method context.
                    throw new errors_1.DecryptionError((0, ttag_1.c)('Error').t `Parent cannot be decrypted`);
                }
                throw error;
            }
        }
        if (node.shareId) {
            return {
                key: await this.shareService.getSharePrivateKey(node.shareId),
            };
        }
        // This is bug that should not happen.
        // API cannot provide node without parent or share.
        throw new Error('Node has neither parent node nor share');
    }
    async getNodeKeys(nodeUid) {
        try {
            return await this.cryptoCache.getNodeKeys(nodeUid);
        }
        catch {
            const { keys } = await this.loadNode(nodeUid);
            if (!keys) {
                throw new errors_1.DecryptionError((0, ttag_1.c)('Error').t `Item cannot be decrypted`);
            }
            return keys;
        }
    }
    async getNodePrivateAndSessionKeys(nodeUid) {
        const node = await this.getNode(nodeUid);
        const { key: parentKey } = await this.getParentKeys(node);
        const { key, passphrase, passphraseSessionKey, contentKeyPacketSessionKey } = await this.getNodeKeys(nodeUid);
        const nameSessionKey = await this.cryptoService.getNameSessionKey(node, parentKey);
        return {
            key,
            passphrase,
            passphraseSessionKey,
            contentKeyPacketSessionKey,
            nameSessionKey,
        };
    }
    async getRootNodeEmailKey(nodeUid) {
        const rootNode = await this.getRootNode(nodeUid);
        if (!rootNode.shareId) {
            throw new Error(`Node "${nodeUid}" is not accessible - missing root shareId`);
        }
        return this.shareService.getContextShareMemberEmailKey(rootNode.shareId);
    }
    async getNodeUrl(nodeUid) {
        const node = await this.getNode(nodeUid);
        if ((0, mediaTypes_1.isProtonDocument)(node.mediaType) || (0, mediaTypes_1.isProtonSheet)(node.mediaType)) {
            const { volumeId, nodeId } = (0, uids_1.splitNodeUid)(nodeUid);
            const type = (0, mediaTypes_1.isProtonDocument)(node.mediaType) ? 'doc' : 'sheet';
            return `https://docs.proton.me/doc?type=${type}&mode=open&volumeId=${volumeId}&linkId=${nodeId}`;
        }
        const rootNode = await this.getRootNode(nodeUid);
        if (!rootNode.shareId) {
            throw new errors_1.ProtonDriveError((0, ttag_1.c)('Error').t `Node is not accessible`);
        }
        const { nodeId } = (0, uids_1.splitNodeUid)(nodeUid);
        const type = node.type === interface_1.NodeType.File ? 'file' : 'folder';
        return `https://drive.proton.me/${rootNode.shareId}/${type}/${nodeId}`;
    }
    async getRootNode(nodeUid) {
        const node = await this.getNode(nodeUid);
        return node.parentUid ? this.getRootNode(node.parentUid) : node;
    }
    ;
}
exports.NodesAccess = NodesAccess;
//# sourceMappingURL=nodesAccess.js.map